import clsx from "clsx";
import React from "react";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { match } from "ts-pattern";

import { FormatDate } from "~/components/common/FormatDate";
import { RemoteLogErrorBoundary } from "~/components/common/remote-log-error-boundary";
import {
  Table,
  TableCell,
  TableEmpty,
  TableError,
  TableHead,
  TableLoading,
  TableRow,
} from "~/components/ui/table";
import { useIntlDocumentTitle } from "~/hooks/use-document-title";
import { useScrollToTop } from "~/hooks/use-scroll-to-top";

import { useGetAgreementsQuery } from "./agreements-api";
import { AgreementStatus, DoctorDocumentType } from "./documents-const";

export function DocumentsPage() {
  useIntlDocumentTitle("top.menu.documents");
  useScrollToTop();

  return (
    <RemoteLogErrorBoundary component="documents_page">
      <AgreementsTable />
    </RemoteLogErrorBoundary>
  );
}

function AgreementsTable() {
  const agreementsQuery = useGetAgreementsQuery();
  const columns = 4;

  return (
    <Table>
      <thead>
        <TableRow>
          <TableHead style={{ minWidth: 200 }}>
            <FormattedMessage id="agreements.table.title" />
          </TableHead>

          <TableHead style={{ minWidth: 100 }}>
            <FormattedMessage id="agreements.table.status" />
          </TableHead>

          <TableHead>
            <FormattedMessage id="date" />
          </TableHead>

          <TableHead>
            <FormattedMessage id="agreements.table.ends_at" />
          </TableHead>
        </TableRow>
      </thead>

      <tbody>
        {match(agreementsQuery)
          .with({ isUninitialized: true }, () => <TableLoading columns={columns} />)
          .with({ isLoading: true }, () => <TableLoading columns={columns} />)
          .with({ isError: true }, () => (
            <TableError columns={columns}>
              <FormattedMessage id="agreements.table.error" />
            </TableError>
          ))
          .with({ isSuccess: true, data: [] }, () => (
            <TableEmpty columns={columns}>
              <FormattedMessage id="agreements.table.not_found" />
            </TableEmpty>
          ))
          .with({ isSuccess: true }, ({ data }) =>
            data.map((doctorDocument) =>
              match(doctorDocument)
                .with(
                  { type: DoctorDocumentType.AGREEMENT },
                  ({ id, agreement, status, action_at, ends_at }) => (
                    <TableRow key={id}>
                      <TableCell>
                        <Link to={`/pages/documents/${id}`}>{agreement.title}</Link>
                      </TableCell>

                      <TableCell>
                        <AgreementStatusBadge status={status} />
                      </TableCell>

                      <TableCell>
                        {action_at ? <FormatDate value={action_at} date /> : null}
                      </TableCell>

                      <TableCell>{ends_at ? <FormatDate value={ends_at} date /> : null}</TableCell>
                    </TableRow>
                  ),
                )
                .with({ type: DoctorDocumentType.OFFER }, ({ offer_id, version, timestamp }) => (
                  <TableRow key={offer_id}>
                    <TableCell>
                      <Link to="/pages/documents/offer">{`Оферта от ${version}`}</Link>
                    </TableCell>

                    <TableCell>
                      <AgreementStatusBadge status={AgreementStatus.ACCEPTED} />
                    </TableCell>

                    <TableCell>
                      <FormatDate value={timestamp} date />
                    </TableCell>

                    <TableCell />
                  </TableRow>
                ))
                .exhaustive(),
            ),
          )
          .otherwise(() => null)}
      </tbody>
    </Table>
  );
}

function AgreementStatusBadge({ status }: { status: AgreementStatus }) {
  const classNames: Record<AgreementStatus, string> = {
    [AgreementStatus.PENDING]: "tw-bg-[#e6bf40]",
    [AgreementStatus.REJECTED]: "tw-bg-[#dc2626]",
    [AgreementStatus.ACCEPTED]: "tw-bg-green-600",
  };

  return (
    <span className={clsx("label label-sm tw-rounded-[3px] tw-font-normal", classNames[status])}>
      <FormattedMessage id={`agreements.table.status.${status}`} />
    </span>
  );
}
